import {
  executeGraphqlRequest,
  readCachedGraphqlQuery,
} from '../apollo/graphql-request';
import { type ProfileCategory, type ProfileSkill } from 'profile/profile.api';

export type PositionStatus = 'DRAFT' | 'PUBLISHED' | 'FILLED' | 'CLOSED' | 'DELETED';
export type PositionBudgetCurrency =
  | 'USD'
  | 'EUR'
  | 'GBP'
  | 'AUD'
  | 'CAD'
  | 'CHF'
  | 'CNY'
  | 'JPY'
  | 'PLN'
  | 'UAH';

export interface PositionLocationCoordinates {
  lat: number;
  lng: number;
}

export interface PositionLocationSummary {
  uuid: string;
  placeId?: string | null;
  label: string;
  coordinates?: PositionLocationCoordinates | null;
  createdAt: string;
  updatedAt: string;
}

export interface PositionSummary {
  uuid: string;
  accountUuid: string;
  eventUuid?: string | null;
  title: string;
  description?: string | null;
  status: PositionStatus;
  location?: PositionLocationSummary | null;
  budgetMin?: number | null;
  budgetMax?: number | null;
  budgetCurrency: PositionBudgetCurrency;
  startsAt?: string | null;
  endsAt?: string | null;
  createdAt: string;
  updatedAt: string;
  account: {
    uuid: string;
    username: string;
  };
  skills: ProfileSkill[];
  categories: ProfileCategory[];
}

export interface CreatePositionInput {
  accountUuid: string;
  eventUuid?: string;
  title: string;
  description?: string;
  location?: {
    placeId?: string | null;
    label: string;
    coordinates?: PositionLocationCoordinates | null;
  } | null;
  budgetMin?: number | null;
  budgetMax?: number | null;
  budgetCurrency?: PositionBudgetCurrency;
  startsAt?: string | null;
  endsAt?: string | null;
  skills?: ProfileSkill[];
  categories?: ProfileCategory[];
}

export interface UpdatePositionInput {
  uuid: string;
  eventUuid?: string | null;
  title: string;
  description?: string;
  location?: {
    placeId?: string | null;
    label: string;
    coordinates?: PositionLocationCoordinates | null;
  } | null;
  budgetMin?: number | null;
  budgetMax?: number | null;
  budgetCurrency?: PositionBudgetCurrency | null;
  startsAt?: string | null;
  endsAt?: string | null;
  skills?: ProfileSkill[];
  categories?: ProfileCategory[];
}

async function executePositionRequest<TData>(
  query: string,
  variables: Record<string, unknown>,
  accessToken?: string
): Promise<TData> {
  return executeGraphqlRequest<TData>(query, variables, {
    accessToken,
    requestName: 'Position',
  });
}

const POSITION_SUMMARY_SELECTION_SET = `
          uuid
          accountUuid
          eventUuid
          title
          description
          status
          budgetMin
          budgetMax
          budgetCurrency
          startsAt
          endsAt
          createdAt
          updatedAt
          account {
            uuid
            username
          }
          skills {
            uuid
            key
            title
          }
`;

const POSITIONS_QUERY = `
      query Positions {
        positions {
${POSITION_SUMMARY_SELECTION_SET}
        }
      }
    `;

const ACCOUNT_POSITIONS_QUERY = `
      query AccountPositions($accountUuid: ID!) {
        accountPositions(accountUuid: $accountUuid) {
${POSITION_SUMMARY_SELECTION_SET}
        }
      }
    `;

const POSITION_QUERY = `
      query Position($uuid: ID!) {
        position(uuid: $uuid) {
${POSITION_SUMMARY_SELECTION_SET}
        }
      }
    `;

const EVENT_POSITIONS_QUERY = `
      query EventPositions($eventUuid: ID!) {
        eventPositions(eventUuid: $eventUuid) {
${POSITION_SUMMARY_SELECTION_SET}
        }
      }
    `;

function normalizePositionSummary(position: PositionSummary): PositionSummary {
  return {
    ...position,
    skills: position.skills ?? [],
    categories: position.categories ?? [],
  };
}

function normalizePositionSummaries(positions: PositionSummary[]): PositionSummary[] {
  return positions.map(normalizePositionSummary);
}

function normalizePositionSkillInputs(
  skills?: ProfileSkill[]
): Array<{ uuid: string; key: string; title: string }> | undefined {
  if (!skills?.length) {
    return undefined;
  }

  const normalizedSkills = skills
    .map((skill) => ({
      uuid: skill.uuid?.trim() ?? '',
      key: skill.key?.trim() ?? '',
      title: skill.title?.trim() ?? '',
    }))
    .filter(
      (skill) =>
        skill.uuid.length > 0 && skill.key.length > 0 && skill.title.length > 0
    );

  return normalizedSkills.length ? normalizedSkills : undefined;
}

function normalizePositionLocationInput(
  location?: CreatePositionInput['location'] | UpdatePositionInput['location']
) {
  if (!location) {
    return location;
  }

  return location.label?.trim() || null;
}

export async function fetchPositions(accessToken?: string): Promise<PositionSummary[]> {
  const response = await executePositionRequest<{ positions: PositionSummary[] }>(
    POSITIONS_QUERY,
    {},
    accessToken
  );

  return normalizePositionSummaries(response.positions);
}

export function readCachedPositions(): PositionSummary[] | undefined {
  const cachedData = readCachedGraphqlQuery<{ positions: PositionSummary[] }>(
    POSITIONS_QUERY
  );

  return cachedData ? normalizePositionSummaries(cachedData.positions) : undefined;
}

export async function fetchAccountPositions(
  accountUuid: string,
  accessToken?: string
): Promise<PositionSummary[]> {
  const response = await executePositionRequest<{ accountPositions: PositionSummary[] }>(
    ACCOUNT_POSITIONS_QUERY,
    { accountUuid },
    accessToken
  );

  return normalizePositionSummaries(response.accountPositions);
}

export function readCachedAccountPositions(
  accountUuid: string
): PositionSummary[] | undefined {
  const cachedData = readCachedGraphqlQuery<{ accountPositions: PositionSummary[] }>(
    ACCOUNT_POSITIONS_QUERY,
    { accountUuid }
  );

  return cachedData ? normalizePositionSummaries(cachedData.accountPositions) : undefined;
}

export async function fetchPositionByUuid(
  uuid: string,
  accessToken?: string
): Promise<PositionSummary | null> {
  const response = await executePositionRequest<{ position: PositionSummary | null }>(
    POSITION_QUERY,
    { uuid },
    accessToken
  );

  return response.position ? normalizePositionSummary(response.position) : null;
}

export function readCachedPositionByUuid(
  uuid: string
): PositionSummary | null | undefined {
  const cachedData = readCachedGraphqlQuery<{ position: PositionSummary | null }>(
    POSITION_QUERY,
    { uuid }
  );

  if (!cachedData) {
    return undefined;
  }

  return cachedData.position ? normalizePositionSummary(cachedData.position) : null;
}

export async function createPositionRequest(
  input: CreatePositionInput,
  accessToken?: string
): Promise<PositionSummary> {
  const normalizedInput = {
    ...input,
    location: normalizePositionLocationInput(input.location),
    skills: normalizePositionSkillInputs(input.skills),
  };

  const response = await executePositionRequest<{ createPosition: PositionSummary }>(
    `
      mutation CreatePosition($input: CreatePositionInput!) {
        createPosition(input: $input) {
          uuid
          accountUuid
          eventUuid
          title
          description
          status
          budgetMin
          budgetMax
          budgetCurrency
          startsAt
          endsAt
          createdAt
          updatedAt
          account {
            uuid
            username
          }
          skills {
            uuid
            key
            title
          }
        }
      }
    `,
    { input: normalizedInput },
    accessToken
  );

  return normalizePositionSummary(response.createPosition);
}

export async function updatePositionRequest(
  input: UpdatePositionInput,
  accessToken?: string
): Promise<PositionSummary> {
  const normalizedInput = {
    ...input,
    location: normalizePositionLocationInput(input.location),
    skills: normalizePositionSkillInputs(input.skills),
  };

  const response = await executePositionRequest<{ updatePosition: PositionSummary }>(
    `
      mutation UpdatePosition($input: UpdatePositionInput!) {
        updatePosition(input: $input) {
          uuid
          accountUuid
          eventUuid
          title
          description
          status
          budgetMin
          budgetMax
          budgetCurrency
          startsAt
          endsAt
          createdAt
          updatedAt
          account {
            uuid
            username
          }
          skills {
            uuid
            key
            title
          }
        }
      }
    `,
    { input: normalizedInput },
    accessToken
  );

  return normalizePositionSummary(response.updatePosition);
}

export async function fetchEventPositions(
  eventUuid: string,
  accessToken?: string
): Promise<PositionSummary[]> {
  const response = await executePositionRequest<{ eventPositions: PositionSummary[] }>(
    EVENT_POSITIONS_QUERY,
    { eventUuid },
    accessToken
  );

  return normalizePositionSummaries(response.eventPositions);
}

export function readCachedEventPositions(
  eventUuid: string
): PositionSummary[] | undefined {
  const cachedData = readCachedGraphqlQuery<{ eventPositions: PositionSummary[] }>(
    EVENT_POSITIONS_QUERY,
    { eventUuid }
  );

  return cachedData ? normalizePositionSummaries(cachedData.eventPositions) : undefined;
}

export async function publishPositionRequest(
  uuid: string,
  accessToken?: string
): Promise<PositionSummary> {
  const response = await executePositionRequest<{ publishPosition: PositionSummary }>(
    `
      mutation PublishPosition($uuid: ID!) {
        publishPosition(uuid: $uuid) {
          uuid
          accountUuid
          title
          description
          status
          budgetMin
          budgetMax
          budgetCurrency
          startsAt
          endsAt
          createdAt
          updatedAt
          account {
            uuid
            username
          }
          skills {
            uuid
            key
            title
          }
        }
      }
    `,
    { uuid },
    accessToken
  );

  return normalizePositionSummary(response.publishPosition);
}

export async function closePositionRequest(
  uuid: string,
  accessToken?: string
): Promise<PositionSummary> {
  const response = await executePositionRequest<{ closePosition: PositionSummary }>(
    `
      mutation ClosePosition($uuid: ID!) {
        closePosition(uuid: $uuid) {
          uuid
          accountUuid
          title
          description
          status
          budgetMin
          budgetMax
          budgetCurrency
          startsAt
          endsAt
          createdAt
          updatedAt
          account {
            uuid
            username
          }
          skills {
            uuid
            key
            title
          }
        }
      }
    `,
    { uuid },
    accessToken
  );

  return normalizePositionSummary(response.closePosition);
}

export async function deletePositionRequest(
  uuid: string,
  accessToken?: string
): Promise<PositionSummary> {
  const response = await executePositionRequest<{ deletePosition: PositionSummary }>(
    `
      mutation DeletePosition($uuid: ID!) {
        deletePosition(uuid: $uuid) {
          uuid
          accountUuid
          eventUuid
          title
          description
          status
          budgetMin
          budgetMax
          budgetCurrency
          startsAt
          endsAt
          createdAt
          updatedAt
          account {
            uuid
            username
          }
          skills {
            uuid
            key
            title
          }
        }
      }
    `,
    { uuid },
    accessToken
  );

  return normalizePositionSummary(response.deletePosition);
}
